<template>
  <RevDrawer
    :name="`${MODAL_NAME}_${listingId}`"
    size="large"
    :title="i18n(translations.title)"
    @close="onClose"
    @open="onOpen"
  >
    <template #body>
      <div>
        <InsuranceSelection
          :insurance-offers
          :listing-id
          :model="deviceName"
          :selected-offer-id="selectedOffer?.id"
          tracking-zone="insurance_cart"
          :with-link="false"
          @remove="removeInsurance"
          @select="addInsurance"
        />

        <div ref="compliancyElement">
          <InsuranceCompliancy
            v-if="selectedOffer"
            id="cart-insurance-modal-compliancy"
            class="mt-16"
            :disabled="false"
            :offer="selectedOffer"
            @update="toggleCompliancy"
          />

          <p v-if="selectedOffer && isError" class="text-static-danger-hi mt-3">
            {{ i18n(translations.compliancyError) }}
          </p>
        </div>

        <RevDivider class="my-24" />
        <BackupDrawerFaq v-if="!hasBackupPlusOffers" />
        <BackupPlusDrawerFaq v-else />
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end">
        <RevButton
          data-qa="atc-insurances-submit"
          :loading="isLoading"
          variant="primary"
          @click="handleSubmit"
        >
          {{ i18n(translations.confirm) }}
        </RevButton>
      </div>
    </template>
  </RevDrawer>
</template>

<script lang="ts" setup>
import { computed, ref, useTemplateRef } from 'vue'

import type {
  InsuranceOffer,
  InsuranceOfferWithSelectedState,
} from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevButton } from '@ds/components/Button'
import { RevDivider } from '@ds/components/Divider'
import { RevDrawer } from '@ds/components/Drawer'
import { closeModal } from '@ds/components/ModalBase'

import InsuranceSelection from '~/scopes/checkout/components/Cart/components/CartItemCard/components/Insurances/components/InsuranceSelection/InsuranceSelection.vue'
import InsuranceCompliancy from '~/scopes/insurance/components/InsuranceCompliancy/InsuranceCompliancy.vue'
import BackupDrawerFaq from '~/scopes/insurance/components/InsuranceOffers/components/InsuranceProvider/components/BackupDrawerFaq/BackupDrawerFaq.vue'
import BackupPlusDrawerFaq from '~/scopes/insurance/components/InsuranceOffers/components/InsuranceProvider/components/BackupPlusDrawerFaq/BackupPlusDrawerFaq.vue'

import { MODAL_NAME } from '../../InsurancesV2.constants'

import translations from './SelectionModal.translations'

const props = defineProps<{
  deviceName: string
  listingId: string
  insuranceOffers: InsuranceOfferWithSelectedState[]
}>()

const emit = defineEmits<{
  update: [updatedInsuranceOffers: InsuranceOfferWithSelectedState]
}>()

const i18n = useI18n()
const $httpFetch = useHttpFetch()
const { trackClick } = useTracking()

const initialSelectedOffer = ref<InsuranceOfferWithSelectedState | undefined>()
const selectedOffer = ref<InsuranceOfferWithSelectedState | undefined>()
const isLoading = ref(false)
const isError = ref(false)
const compliancyElement = useTemplateRef<HTMLElement>('compliancyElement')

const hasBackupPlusOffers = computed(() => {
  return props.insuranceOffers.some((offer) => offer.isBackupPlus) ?? false
})

function addInsurance(offer: InsuranceOffer) {
  isError.value = false
  selectedOffer.value = {
    ...offer,
    selected: true,
  }
}

function removeInsurance() {
  isError.value = false
  selectedOffer.value = undefined
}

function toggleCompliancy(offer: InsuranceOffer) {
  isError.value = false
  selectedOffer.value = {
    ...offer,
    selected: selectedOffer.value?.selected ?? false,
  }
}

async function handleSubmit() {
  if (selectedOffer.value) {
    try {
      trackClick({
        name: 'add_protection',
        zone: 'insurance_cart',
      })

      if (
        selectedOffer.value.compliancy.isUserAgreementRequired &&
        !selectedOffer.value.compliancy.isUserAgreementAccepted
      ) {
        if (compliancyElement.value) {
          compliancyElement.value.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }

        trackClick({
          name: 'continue',
          zone: 'insurance_cart',
          has_compliancy_error: true,
        })

        isError.value = true

        return
      }

      isLoading.value = true

      await $httpFetch('/bm/cart/update_insurance_offer', {
        method: 'POST',
        body: {
          listing_id: `${props.listingId}`,
          insurance_offer_id: `${selectedOffer.value.id}`,
        },
      })

      await $httpFetch('/bm/cart/insurances/agreement', {
        method: 'POST',
        body: {
          listingId: `${props.listingId}`,
          insuranceOfferId: `${selectedOffer.value.id}`,
          isAccepted: selectedOffer.value.compliancy.isUserAgreementAccepted,
        },
      })

      emit('update', selectedOffer.value)
      isLoading.value = false
      closeModal()
    } catch {
      isLoading.value = false
    }
  } else if (initialSelectedOffer.value) {
    try {
      isLoading.value = true

      trackClick({
        name: 'remove_protection',
        zone: 'insurance_cart',
      })

      await $httpFetch('/bm/cart/update_insurance_offer', {
        method: 'POST',
        body: {
          listing_id: props.listingId,
          insurance_offer_id: null,
        },
      })

      emit('update', {
        ...initialSelectedOffer.value,
        selected: false,
      })

      isLoading.value = false
      closeModal()
    } catch {
      isLoading.value = false
    }
  } else {
    trackClick({
      name: 'skip_protection',
      zone: 'insurance_cart',
    })

    closeModal()
  }
}

function onOpen() {
  initialSelectedOffer.value =
    props.insuranceOffers.find((offer) => offer.selected) ?? undefined
  selectedOffer.value = initialSelectedOffer.value
}

function onClose() {
  initialSelectedOffer.value = undefined
  selectedOffer.value = undefined
}
</script>
